import {
  AUXILIARY_ORG_TYPES,
  FinancialAccountNode,
  OrgNode,
  OrgType,
} from './interfaces';
import { StripeEnv } from '../../util/enums';

export default async function crawlAncestorHierarchy(
  rootOrg: OrgNode,
  financialAccountAncestors: FinancialAccountNode[],
  provider: VaultProvider,
  stripeEnv?: StripeEnv,
  payableOrgId?: string,
): Promise<FinancialAccountNode | undefined> {
  if (AUXILIARY_ORG_TYPES.includes(rootOrg.type.toUpperCase() as OrgType)) {
    return;
  }

  if (stripeEnv === undefined) {
    stripeEnv = StripeEnv.RAISE;
  }

  // if no financial account is found, look up the tree
  const groupedAncestors: {
    orgType: OrgType;
    financialAccounts: FinancialAccountNode[];
  }[] = [];

  // group ancestors by org type
  for (const financialAccountAncestor of financialAccountAncestors) {
    if (financialAccountAncestor.orgType === undefined) {
      continue;
    }
    const group = groupedAncestors.find(
      (ga) => ga.orgType === financialAccountAncestor.orgType,
    );
    if (group !== undefined) {
      group.financialAccounts.push(financialAccountAncestor);
    } else {
      groupedAncestors.push({
        orgType: financialAccountAncestor.orgType,
        financialAccounts: [financialAccountAncestor],
      });
    }
  }

  // iterate over ancestors
  let ancestorFinancialAccount: FinancialAccountNode | undefined;
  const ancestorFilter = (
    financialAccount: FinancialAccountNode,
    filterByStripeEnv: boolean,
  ): boolean => {
    return (
      // fa matches payment processor
      financialAccount.processor === provider.toUpperCase() &&
      // fa is not deleted
      financialAccount.status !== 'TERMINATED' &&
      // fa acctId is defined
      !!financialAccount.acctId &&
      // allow optional filtering by stripeEnv
      ((filterByStripeEnv && financialAccount.stripeEnv === stripeEnv) ||
        true) &&
      // for districts, confirm they match the payableOrgId
      ((financialAccount.orgType === 'DISTRICT' &&
        financialAccount.orgId === payableOrgId) ||
        financialAccount.orgType !== 'DISTRICT')
    );
  };
  for (const { orgType, financialAccounts } of groupedAncestors) {
    // attempt to find account with matching stripeEnv first
    ancestorFinancialAccount = financialAccounts.find((fa) =>
      ancestorFilter(fa, true),
    );

    // if not found, attempt to find any account
    if (ancestorFinancialAccount === undefined) {
      ancestorFinancialAccount = financialAccounts.find((fa) =>
        ancestorFilter(fa, false),
      );
    }
    if (
      ancestorFinancialAccount !== undefined ||
      AUXILIARY_ORG_TYPES.includes(orgType)
    ) {
      break;
    }
  }

  return ancestorFinancialAccount;
}
